import { createConsumer } from '@rails/actioncable'
import { bindWishlistButtons } from '../../addToWishlist'
import { initLazyLoadedImages } from '../../listing_tile_rendering/listingImageRenderer'
import { t } from './config'
import { escapeHTML } from './richText'
import { createConversation, sendMessage, interrupt, sendListingFeedback, sendReplyFeedback, closeConversation, isRateLimited } from './api'
import createConversationSubscription from './conversation'
import createTranscript from './transcript'
import createAttachments from './attachments'
import { createFeedback } from './feedback'
import { createReplyFeedback } from './replyFeedback'
import { bindCarousels } from './widgets'
import { trackVera, trackListingFeedback, bindResultTracking, resultsViewedProperties } from './tracking'
import { prepareWishlist, rememberWishlistIntent } from './wishlist'

// onConversationsChanged runs when the shopper's list of conversations may have changed.
export default function createChat (root, { onConversationsChanged = () => {} } = {}) {
  const transcriptElement = root.querySelector('.vjs-vera-transcript')
  const feedback = createFeedback(transcriptElement, payload => submitFeedback(payload))
  const replyFeedback = createReplyFeedback(transcriptElement, payload => submitReplyFeedback(payload))
  // Tiles past the sixth render lazy, so a "show more" page needs the observer.
  const transcript = createTranscript(transcriptElement, {
    renderFeedback: feedback.html,
    onWidgets: () => {
      initLazyLoadedImages()
      bindWishlistButtons(transcriptElement)
    }
  })
  const intro = root.querySelector('.vjs-vera-intro')
  const input = root.querySelector('.vjs-vera-input')
  const form = root.querySelector('.vjs-vera-composer')
  const sendButton = root.querySelector('.vjs-vera-send')
  const fileInput = root.querySelector('.vjs-vera-file')
  const growWrap = root.querySelector('.vjs-vera-input-grow')
  const composerError = root.querySelector('.vjs-vera-composer-error')
  const queued = root.querySelector('.vjs-vera-queued')
  const attachments = createAttachments(root.querySelector('.vjs-vera-attachments'), {
    onError: message => showComposerError(message),
    onChange: () => syncSendButton()
  })
  const viewedWidgets = new Set()

  // Inert until the first subscription: no socket, no timers, no listeners.
  const consumer = createConsumer()
  let conversation = null
  let pendingConversation = null
  let pendingSend
  let wishlistReady
  let running = false
  let live = false
  let messagesSent = 0
  let lastText = ''
  let lastAttachments = []
  let lockedScrollY
  let statusElement

  function isOpen () {
    return !root.hidden
  }

  function composerEmpty () {
    return !input.value.trim() && attachments.ids.length === 0
  }

  root.querySelectorAll('.vjs-vera-dismiss').forEach(element => {
    element.addEventListener('click', () => close())
  })

  root.querySelector('.vjs-vera-opt-out').addEventListener('submit', () => {
    trackVera('Search Turned Off', { source: 'chat', vera_conversation_id: conversation?.id })
  })

  // A wishlist or login modal over the chat is the top layer: Escape closes that first.
  document.addEventListener('keydown', event => {
    if (event.key !== 'Escape' || !isOpen()) return

    const modal = document.querySelector('.vjs-modal.show')
    if (modal) return modal.classList.remove('show')
    close()
  })

  form.addEventListener('submit', event => {
    event.preventDefault()
    if (running && composerEmpty()) return stop()
    send(input.value)
  })

  input.addEventListener('keydown', event => {
    if (event.key !== 'Enter' || event.shiftKey) return
    event.preventDefault()
    if (composerEmpty()) return
    form.requestSubmit()
  })

  input.addEventListener('input', () => {
    resizeInput()
    syncSendButton()
  })

  root.querySelectorAll('.vjs-vera-suggestion').forEach(button => {
    button.addEventListener('click', () => send(button.textContent.trim(), 'suggestion'))
  })

  root.querySelector('.vjs-vera-attach').addEventListener('click', () => fileInput.click())

  fileInput.addEventListener('change', () => {
    const [file] = fileInput.files
    fileInput.value = ''
    attach(file)
  })

  async function attach (file) {
    if (file && await attachments.add(() => conversationId(), file)) {
      trackVera('Photo Attached', { vera_conversation_id: conversation?.id })
    }
  }

  // A photo dropped anywhere on the chat attaches; a near miss would otherwise open it in the tab.
  // Counted, as dragenter and dragleave fire for every child crossed.
  let dragDepth = 0
  const draggingFiles = event => Array.from(event.dataTransfer?.types || []).includes('Files')

  function endDrag () {
    dragDepth = 0
    form.classList.remove('is-dropping')
  }

  root.addEventListener('dragenter', event => {
    if (!draggingFiles(event)) return
    dragDepth += 1
    form.classList.add('is-dropping')
  })

  root.addEventListener('dragleave', event => {
    if (draggingFiles(event) && --dragDepth <= 0) endDrag()
  })

  root.addEventListener('dragover', event => {
    if (!draggingFiles(event)) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
  })

  root.addEventListener('drop', event => {
    if (!draggingFiles(event)) return
    event.preventDefault()
    endDrag()
    attach(event.dataTransfer.files[0])
    input.focus()
  })

  transcriptElement.addEventListener('click', event => {
    const login = event.target.closest('.vjs-wishlist-login')
    if (login && conversation) rememberWishlistIntent(conversation.id, login.dataset.listingId)

    const action = event.target.closest('.vjs-vera-action')
    if (action) return runAction(action)

    if (event.target.closest('.vjs-vera-retry')) retry()
  })

  transcriptElement.addEventListener('focusin', event => {
    if (event.target.closest('.vjs-vera-note')) root.classList.add('is-noting')
  })
  transcriptElement.addEventListener('focusout', event => {
    if (event.target.closest('.vjs-vera-note')) root.classList.remove('is-noting')
  })

  bindCarousels(transcriptElement)
  bindResultTracking(transcriptElement, () => conversation?.id)

  async function open ({ conversationId, seedText, entryPoint } = {}) {
    if (!isOpen()) {
      trackVera('Chat Opened', { entry_point: entryPoint, vera_conversation_id: conversationId, resumed: Boolean(conversationId) })
    }
    root.hidden = false
    lockPage()
    trackViewport(true)

    // Hearts render filled from the page's store of wishlisted listings.
    if (!wishlistReady) wishlistReady = prepareWishlist()
    await wishlistReady
    if (conversationId !== conversation?.id) await load(conversationId)
    if (seedText) await send(seedText, 'search_box')

    focusComposer()
  }

  function close () {
    if (isOpen()) trackVera('Chat Closed', { vera_conversation_id: conversation?.id, messages_sent: messagesSent })
    root.hidden = true
    unlockPage()
    trackViewport(false)
    onConversationsChanged()
  }

  // iOS scrolls the page under a fixed dialog despite overflow: hidden. Pin the page in place.
  // iOS keyboard handling: docs/vera/chat-mobile.md.
  function lockPage () {
    if (lockedScrollY !== undefined) return

    lockedScrollY = window.scrollY
    document.body.style.top = `-${lockedScrollY}px`
    document.body.classList.add('vsd-vera-open')
  }

  function unlockPage () {
    if (lockedScrollY === undefined) return

    document.body.classList.remove('vsd-vera-open')
    document.body.style.removeProperty('top')
    window.scrollTo({ top: lockedScrollY, behavior: 'instant' })
    lockedScrollY = undefined
  }

  // On a phone, focus raises the keyboard over half the chat: wait for a tap.
  function focusComposer () {
    if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) input.focus()
  }

  // iOS lays out fixed elements against the page, not above the keyboard: follow the visual viewport.
  function trackViewport (tracking) {
    const viewport = window.visualViewport
    if (!viewport) return

    const method = tracking ? 'addEventListener' : 'removeEventListener'
    viewport[method]('resize', fitToViewport)
    viewport[method]('scroll', fitToViewport)

    if (tracking) return fitToViewport()
    root.style.removeProperty('--vera-viewport-top')
    root.style.removeProperty('--vera-viewport-height')
  }

  // Keep what sat above the composer in place as the keyboard opens; keep a focused transcript field in view.
  function fitToViewport () {
    const { offsetTop, height } = window.visualViewport
    const fromBottom = transcriptElement.scrollHeight - transcriptElement.scrollTop - transcriptElement.clientHeight

    root.style.setProperty('--vera-viewport-top', `${offsetTop}px`)
    root.style.setProperty('--vera-viewport-height', `${height}px`)

    // Scrolled by hand: scrollIntoView would move the page underneath as well.
    const field = transcriptElement.contains(document.activeElement) ? document.activeElement : null
    if (field) {
      const kept = field.closest('.vjs-vera-feedback-panel, .vjs-vera-reply-feedback-panel') || field
      const overflow = kept.getBoundingClientRect().bottom - transcriptElement.getBoundingClientRect().bottom
      if (overflow > 0) transcriptElement.scrollTop += overflow
      return
    }
    transcriptElement.scrollTop = transcriptElement.scrollHeight - transcriptElement.clientHeight - fromBottom
  }

  async function load (id) {
    conversation?.disconnect()
    pendingConversation = null
    transcript.clear()
    attachments.reset()
    clearQueued()
    feedback.reset()
    setRunning(false)
    messagesSent = 0
    lastText = ''
    lastAttachments = []
    viewedWidgets.clear()
    // A resumed transcript replays as it loads: only events after the shopper acts count as seen.
    live = false

    if (!id) {
      conversation = null
      showIntro(true)
      return
    }

    connectTo(id)
    showIntro(false)
  }

  function connectTo (id) {
    const subscription = createConversationSubscription(consumer, id, event => {
      if (conversation === subscription) apply(event)
    })
    conversation = subscription
    subscription.connect()
  }

  // Memoised on the promise so concurrent callers share one create. Failures are not memoised.
  function conversationId () {
    if (conversation) return Promise.resolve(conversation.id)
    if (pendingConversation) return pendingConversation

    const pending = createConversation().then(({ id }) => {
      if (pendingConversation === pending) {
        pendingConversation = null
        connectTo(id)
      }
      return id
    }, error => {
      if (pendingConversation === pending) pendingConversation = null
      throw error
    })

    pendingConversation = pending
    return pending
  }

  function retry () {
    send(lastText, 'retry', lastAttachments || [])
  }

  async function send (text, source = 'composer', resent = null) {
    const body = (text || '').trim()
    if (!body && (resent || attachments.pending).length === 0) return

    // Off the composer before the post: what the shopper adds meanwhile is the next message.
    const draft = source === 'composer' ? takeDraft() : null
    const sent = resent || attachments.take()
    const followUp = running
    live = true
    lastText = body
    lastAttachments = sent
    showIntro(false)
    showComposerError('')
    setRunning(true)

    pendingSend = deliver(body, sent, followUp, source, draft)
    await pendingSend
  }

  function takeDraft () {
    const draft = input.value
    input.value = ''
    resizeInput()
    return draft
  }

  function restoreDraft (draft, restored) {
    if (draft && !input.value.trim()) {
      input.value = draft
      resizeInput()
    }
    attachments.restore(restored)
    syncSendButton()
  }

  // Tracked before the post, so the Browser SDK has refreshed the visit the
  // server reads off its cookie for the agent's own events.
  async function deliver (body, sent, followUp, source, draft) {
    const attachmentIds = sent.map(attachment => attachment.id)
    let id
    try {
      id = await conversationId()
      if (conversation?.id !== id) return
      const firstMessage = messagesSent === 0 && !transcriptElement.querySelector('.vjs-vera-user-turn')
      messagesSent += 1
      trackVera('Message Sent', {
        vera_conversation_id: id,
        source,
        is_first_message: firstMessage,
        has_text: Boolean(body),
        attachment_count: attachmentIds.length,
        follow_up: followUp
      })
      const response = await sendMessage(id, body, attachmentIds, source === 'retry')
      const { message_id: messageId, held } = await response.json()
      if (conversation?.id !== id) return
      // Delivered already if the running turn ended before this response came back.
      if (held && !transcript.hasUserMessage(messageId)) showQueued(messageId, body)
      // The first message gives the conversation its title, which makes it resumable.
      onConversationsChanged()
    } catch (error) {
      trackVera('Message Failed', { vera_conversation_id: id, follow_up: followUp, rate_limited: isRateLimited(error) })
      if (id && conversation?.id !== id) return
      // A resent photo already belongs to the message that failed: it stays out of the composer.
      restoreDraft(draft, source === 'retry' ? [] : sent)
      const message = isRateLimited(error) ? t('errors.rate_limited') : t('errors.connection')
      if (followUp) return showComposerError(message)
      failTurn(message)
    }
  }

  // Queued above the composer; joins the transcript once delivered.
  function showQueued (messageId, text) {
    queued.insertAdjacentHTML('beforeend', `
      <p class="vsd-vera__queued-item" data-message-id="${escapeHTML(messageId)}">
        <span class="vsd-vera__queued-label">${t('queued')}</span>
        <span class="vsd-vera__queued-text">${escapeHTML(text)}</span>
      </p>
    `)
    queued.hidden = false
  }

  function dequeue (messageId) {
    queued.querySelector(`[data-message-id="${escapeHTML(messageId)}"]`)?.remove()
    queued.hidden = queued.children.length === 0
  }

  function clearQueued () {
    queued.innerHTML = ''
    queued.hidden = true
  }

  // A link action (the full results page) opens on its own; only "new search" acts here.
  function runAction (action) {
    const actionId = action.dataset.actionId
    trackVera('Action Clicked', {
      vera_conversation_id: conversation?.id,
      action_id: actionId,
      vera_result_set_id: action.closest('.vjs-vera-actions')?.dataset.resultSetId || undefined
    })
    if (actionId === 'new_search') return startOver()
  }

  function failTurn (message = t('errors.connection')) {
    setRunning(false)
    showComposerError(message)
  }

  async function startOver () {
    const previous = conversation
    if (previous) {
      try {
        await closeConversation(previous.id)
      } catch {
        return showComposerError(t('errors.connection'))
      }
    }
    await load(null)
    onConversationsChanged()
    focusComposer()
  }

  // A stop on the first message lands before the conversation exists: wait for the
  // send, or the interrupt would race the turn it is meant to cancel.
  async function stop () {
    await pendingSend
    if (!running) return
    if (!conversation) return setRunning(false)

    trackVera('Stop Clicked', { vera_conversation_id: conversation.id })
    try {
      await interrupt(conversation.id)
    } catch {
      failTurn()
    }
  }

  async function submitFeedback (payload) {
    const tile = transcriptElement.querySelector(`[data-widget-id="${payload.widget_id}"] .vjs-vera-result[data-productid="${payload.listing_id}"]`)
    trackListingFeedback(conversation?.id, tile, payload)
    try {
      await sendListingFeedback(await conversationId(), payload)
    } catch {
      showComposerError(t('errors.connection'))
    }
  }

  async function submitReplyFeedback (payload) {
    // Once per rating, on the verdict: the note that may follow only updates Phoenix.
    if (payload.note === undefined) {
      trackVera('Reply Feedback Given', { vera_conversation_id: conversation?.id, verdict: payload.verdict })
    }
    try {
      await sendReplyFeedback(await conversationId(), payload)
    } catch {
      showComposerError(t('errors.connection'))
    }
  }

  function turnDone (data) {
    setRunning(false)
    if (data.message_id) transcript.replyFeedback(data.message_id, replyFeedback.html(data.message_id))
  }

  function apply (event) {
    applyEvent(event)
    if (!live) transcript.settleAtEnd()
  }

  function applyEvent ({ type, data }) {
    switch (type) {
      case 'vera.user.message':
        dequeue(data.message_id)
        return transcript.userMessage(data)
      case 'vera.turn.start': return setRunning(true)
      case 'vera.message.start': return streaming(() => transcript.messageStart(data))
      case 'vera.message.delta': return streaming(() => transcript.messageDelta(data))
      case 'vera.message.done': return transcript.messageDone(data)
      case 'vera.widget': return renderWidget(data)
      case 'vera.listing.feedback': return feedback.show(data)
      case 'vera.status': return status(data)
      case 'vera.error': return error(data)
      case 'vera.turn.done': return turnDone(data)
    }
  }

  // The reply replaces the indicator; a later tool call's status brings it back.
  function streaming (render) {
    statusElement?.remove()
    render()
  }

  function error (data) {
    if (live) trackVera('Reply Failed', { vera_conversation_id: conversation?.id, code: data.code, retriable: data.retriable })
    transcript.error(data)
  }

  function renderWidget (data) {
    transcript.widget(data)
    trackResultsViewed(data)
  }

  function trackResultsViewed (data) {
    if (data.kind !== 'results' || !live || viewedWidgets.has(data.widget_id)) return
    if (!data.payload?.listings?.length) return

    viewedWidgets.add(data.widget_id)
    trackVera('Results Viewed', { vera_conversation_id: conversation?.id, ...resultsViewedProperties(data) })
  }

  // Advisory copy only: the turn ends on turn.done.
  function status ({ state, label }) {
    const node = statusNode()
    if (state === 'ready' || state === 'error') return node.remove()

    node.querySelector('.vjs-vera-status-label').textContent = label || ''
    node.classList.toggle('has-label', Boolean(label))
    transcriptElement.appendChild(node)
    transcript.scrollToEnd()
  }

  function statusNode () {
    if (!statusElement) {
      statusElement = document.createElement('div')
      statusElement.className = 'vsd-vera-turn vsd-vera-turn--assistant vsd-vera-status'
      statusElement.innerHTML = `
        <span class="vsd-vera-turn__label">${t('assistant')}</span>
        <span class="vsd-vera-status__dots" aria-hidden="true"><i></i><i></i><i></i></span>
        <span class="vsd-vera-status__label vjs-vera-status-label"></span>
      `
      statusElement.setAttribute('aria-label', t('thinking'))
    }
    return statusElement
  }

  function setRunning (value) {
    running = value
    syncSendButton()
    if (!running) statusElement?.remove()
  }

  function syncSendButton () {
    const stops = running && composerEmpty()
    sendButton.classList.toggle('is-running', stops)
    sendButton.setAttribute('aria-label', t(stops ? 'stop' : 'send'))
  }

  function showIntro (visible) {
    intro.hidden = !visible
    root.classList.toggle('is-conversing', !visible)
    root.querySelector('.vjs-vera-disclaimer').hidden = !visible
  }

  // The box grows in CSS from this mirrored value.
  function resizeInput () {
    growWrap.dataset.replicatedValue = input.value
  }

  function showComposerError (message) {
    composerError.textContent = message
    composerError.hidden = !message
  }

  return {
    open,
    close,
    transcript,
    get isOpen () {
      return isOpen()
    },
    get conversation () {
      return conversation
    }
  }
}
